import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { Question } from "../../model/question";
import CuvoInputLabel from "../common/CuvoInputLabel";
import "./styles.css";
import * as Yup from "yup";
import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import { TYPES } from "./constants";
import AddIcon from "@mui/icons-material/Add";
import { cloneDeep, noop, omit, values } from "lodash";
import uuid from "react-uuid";
import CloseIcon from "@mui/icons-material/Close";
import { Http, appLogger } from "../../utils";
import { Intent } from "../../model/intent";
import { Qualifier } from "../../model/qualifier";
import { withSnackbar } from "../../utils/common/SnackbarHoc";

const Questions = ({ snackbarShowMessage }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [intentList, setIntentList] = useState<Intent[]>([]);
  const [qualifierList, setQualifierList] = useState<Qualifier[]>([]);
  const [questionsList, setQuestionsList] = useState<Question[]>([]);
  const [selectedQualifier, setSelectedQualifier] = useState<string>("");
  const [selectedIntent, setSelectedIntent] = useState<string>("");
  const { userInfo } = useContext(UserContext) as UserLoginInfo;
  const orgId = userInfo?.userDetails?.orgId || "";

  const initialValues: Question = {
    questionText: "",
    helpText: "",
    type: "",
    defaultValue: "",
    placeholder: "",
    isPII: false,
    options: [{ id: uuid(), value: "" }],
    qualifiers: [],
    intents: [],
  };

  const validationSchema = Yup.object({
    id: Yup.string(),
    questionText: Yup.string().required("Question text is required"),
    type: Yup.string().required("Type is required"),
    isPII: Yup.bool().required(),
  });

  const formik = useFormik<Question>({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });

  const getQuestions = () => {
    if (!orgId || isLoading) return;

    setIsLoading(true);
    Http.get({
      url: `/api/v2/questions/${orgId}`,
      state: userInfo,
    })
      .then((response: any) => {
        setQuestionsList(response.questions);
      })
      .catch((error) => {
        appLogger.error({
          errObj: {
            method: "getQuestions",
            component: "questions",
            fileName: "index.tsx",
            err: error,
          },
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const createQuestion = async (formValues: Question) => {
    try {
      setIsLoading(true);
      await Http.post({
        url: `/api/v2/questions`,
        state: userInfo,
        body: {
          question: {
            ...formValues,
            orgId,
          },
        },
      });
      getQuestions();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "createQualifier",
          component: "qualifiers",
          fileName: "index.tsx",
          err: error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuestion = async (formValues: Question) => {
    try {
      setIsLoading(true);
      await Http.put({
        url: `/api/v2/questions`,
        state: userInfo,
        body: {
          question: {
            ...formValues,
          },
        },
      });
      getQuestions();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "updateQuestion",
          component: "questions",
          fileName: "index.tsx",
          err: error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQualifierChange = (e: SelectChangeEvent) => {
    const { value } = e.target;
    setSelectedQualifier(value);
  };

  const handleIntentChange = (e: SelectChangeEvent) => {
    const { value } = e.target;
    setSelectedIntent(value);
  };

  const handleAddOption = () => {
    const options = formik.values.options
      ? cloneDeep(formik.values.options)
      : [];
    options.push({ id: uuid(), value: "" });
    formik.setFieldValue("options", options).then().catch(noop);
  };

  const handleDeleteOption = (id?: string) => {
    if (!formik.values.options || !id) return;

    const options = cloneDeep(formik.values.options);
    const filteredOptions = options.filter((option) => option.id !== id);
    formik.setFieldValue("options", filteredOptions).then().catch(noop);
  };

  const handleAddQualifier = () => {
    if (!selectedQualifier) return;

    const qualifiers = formik.values.qualifiers
      ? cloneDeep(formik.values.qualifiers)
      : [];
    qualifiers.push(selectedQualifier);
    formik.setFieldValue("qualifiers", qualifiers).then().catch(noop);
    setSelectedQualifier("");
  };

  const handleDeleteQualifier = (id?: string) => {
    if (!formik.values.qualifiers || !id) return;

    const qualifiers = cloneDeep(formik.values.qualifiers);
    const filteredQualifiers = qualifiers.filter(
      (qualifierId) => qualifierId !== id
    );
    formik.setFieldValue("qualifiers", filteredQualifiers).then().catch(noop);
  };

  const handleAddIntent = () => {
    if (!selectedIntent) return;

    const intents = formik.values.intents
      ? cloneDeep(formik.values.intents)
      : [];
    intents.push(selectedIntent);
    formik.setFieldValue("intents", intents).then().catch(noop);
    setSelectedIntent("");
  };

  const handleDeleteIntent = (id?: string) => {
    if (!formik.values.intents || !id) return;

    const intents = cloneDeep(formik.values.intents);
    const filteredIntents = intents.filter((intentId) => intentId !== id);
    formik.setFieldValue("intents", filteredIntents).then().catch(noop);
  };

  const handleEditQuestion = async (question: Question) => {
    await formik.setValues(question);
  };

  const handleDeleteQuestion = async (id?: string) => {
    if (!id) return;

    try {
      setIsLoading(true);
      await Http.deleteReq({
        url: `/api/v2/questions/${id}`,
        state: userInfo,
      });
      getQuestions();
    } catch (error) {
      appLogger.error({
        errObj: {
          method: "handleDeleteQuestion",
          component: "questions",
          fileName: "index.tsx",
          err: error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getQualifiers = () => {
    if (!orgId || isLoading) return;

    setIsLoading(true);
    Http.get({
      url: `/api/v2/qualifiers/${orgId}`,
      state: userInfo,
    })
      .then((response: any) => {
        setQualifierList(response.qualifiers);
        formik.resetForm();
      })
      .catch((error) => {
        appLogger.error({
          errObj: {
            method: "getQualifiers",
            component: "questions",
            fileName: "index.tsx",
            err: error,
          },
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getIntents = () => {
    if (!orgId || isLoading) return;

    setIsLoading(true);
    Http.get({
      url: `/api/v2/intents/${orgId}`,
      state: userInfo,
    })
      .then((response: any) => {
        setIntentList(response.intents);
      })
      .catch((error) => {
        appLogger.error({
          errObj: {
            method: "getIntents",
            component: "questions",
            fileName: "index.tsx",
            err: error,
          },
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreateOrUpdateQuestion = async () => {
    if (!orgId) {
      return snackbarShowMessage("Missing Organization ID", "error");
    }

    const formikErrors = await formik.validateForm();
    const errors: any = values(formikErrors);
    if (errors.length) {
      return snackbarShowMessage(errors[0], "error");
    }

    const formValues = cloneDeep(formik.values);

    if (["SHORT_TEXT", "LONG_TEXT"].includes(formik.values.type)) {
      formValues.options = [];
    } else if (
      formValues.options &&
      formValues.options.length === 1 &&
      formValues.options[0].value === ""
    ) {
      return snackbarShowMessage("Add at least one option", "error");
    }

    if (formValues.id) {
      await updateQuestion(formValues);
      formik.resetForm();
      return;
    }

    await createQuestion(formValues);
    formik.resetForm();
  };

  useEffect(() => {
    if (!orgId) return;

    getIntents();
    getQualifiers();
    getQuestions();
  }, [orgId]);

  return (
    <Box>
      <Box className="question-form">
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={8}
          sx={{
            border: "1px solid #ced3da",
            borderRadius: "8px",
            mb: "30px",
          }}
        >
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={{
              padding: "8px 20px",
              borderBottom: "1px solid #ced3da",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#092146",
              }}
            >
              {formik.values.id ? "Update Question" : "Create Question"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                disabled={isLoading}
                sx={{
                  fontWeight: 600,
                  border: "1px solid #ced3da",
                  borderRadius: "100px!important",
                }}
                variant="outlined"
                color="warning"
                onClick={formik.handleReset}
              >
                Reset
              </Button>
              <Button
                type="button"
                disabled={isLoading}
                sx={{
                  fontWeight: 600,
                  color: "#2858D3",
                  border: "1px solid #ced3da",
                  borderRadius: "100px!important",
                }}
                variant="outlined"
                onClick={handleCreateOrUpdateQuestion}
              >
                {formik.values.id ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>

          <Grid
            container
            gap={2.5}
            sx={{
              padding: "16px 20px",
              width: "100%",
              flexDirection: "column",
              marginBottom: "4px",
            }}
          >
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Question text" showRequiredIndicator />
              <OutlinedInput
                type="text"
                name="questionText"
                value={formik.values.questionText}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl>
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Help text" />
              <OutlinedInput
                type="text"
                name="helpText"
                value={formik.values.helpText}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl>
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Type" showRequiredIndicator />
              <Select
                fullWidth
                sx={{
                  height: "38px",
                  borderColor: "#ced3da",
                  borderRadius: "8px",
                }}
                name="type"
                onChange={formik.handleChange}
                value={formik.values.type}
              >
                {TYPES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Default value" />
              <OutlinedInput
                type="text"
                name="defaultValue"
                value={formik.values.defaultValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl>
            <FormControl className="custom-select" fullWidth>
              <CuvoInputLabel label="Placeholder" />
              <OutlinedInput
                type="text"
                name="placeholder"
                value={formik.values.placeholder}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl> */}

            {/* Options */}
            {["RADIO", "CHECKBOX", "DROPDOWN"].includes(formik.values.type) && (
              <Grid
                container
                flexDirection="column"
                sx={{
                  border: "1px solid #ced3da",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    padding: "8px 16px",
                    marginBottom: "10px",
                    borderBottom: "1px solid #ced3da",
                  }}
                >
                  Options:
                </Box>
                <Grid
                  display="flex"
                  flexDirection="column"
                  gap={2.5}
                  sx={{ padding: "8px 16px 12px" }}
                >
                  {formik.values.options?.length
                    ? formik.values.options.map((option, index) => {
                        return (
                          <Grid
                            key={option.id}
                            item
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <FormControl className="custom-select" fullWidth>
                              <CuvoInputLabel label={`Option ${index + 1}`} />
                              <OutlinedInput
                                type="text"
                                name={`options[${index}].value`}
                                value={option.value}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            </FormControl>
                            <Tooltip title="Delete Option" placement="top">
                              <CloseIcon
                                fontSize="small"
                                className="delete-icon"
                                onClick={() => handleDeleteOption(option.id)}
                              />
                            </Tooltip>
                          </Grid>
                        );
                      })
                    : null}
                  <Button
                    size="small"
                    sx={{
                      marginTop: -1,
                      fontSize: "12px",
                      width: "fit-content",
                    }}
                    onClick={handleAddOption}
                  >
                    <AddIcon fontSize="small" /> Add Option
                  </Button>
                </Grid>
              </Grid>
            )}

            {/* Qualifiers */}
            <Grid
              container
              flexDirection="column"
              sx={{
                border: "1px solid #ced3da",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 16px",
                  marginBottom: "10px",
                  borderBottom: "1px solid #ced3da",
                }}
              >
                Qualifiers:
              </Box>
              <Grid
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{ padding: "8px 16px 12px" }}
              >
                <Grid
                  item
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    paddingBottom: "8px",
                  }}
                >
                  <FormControl className="custom-select" fullWidth>
                    <CuvoInputLabel label="Qualifier" />
                    <Select
                      fullWidth
                      sx={{
                        height: "38px",
                        borderColor: "#ced3da",
                        borderRadius: "8px",
                      }}
                      name="qualifier"
                      onChange={handleQualifierChange}
                      value={selectedQualifier}
                    >
                      {qualifierList &&
                        qualifierList
                          .filter(
                            (q: Qualifier) =>
                              q.id && !formik.values.qualifiers?.includes(q.id)
                          )
                          .map((q) => (
                            <MenuItem key={q.id} value={q.id}>
                              {q.name}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                  <Button
                    disabled={!selectedQualifier}
                    sx={{ minHeight: "36px" }}
                    variant="outlined"
                    onClick={handleAddQualifier}
                  >
                    Add
                  </Button>
                </Grid>

                {formik.values.qualifiers &&
                  qualifierList &&
                  qualifierList
                    .filter(
                      (q: Qualifier) =>
                        q.id && formik.values.qualifiers?.includes(q.id)
                    )
                    .map((qualifier) => {
                      return (
                        <Grid
                          key={qualifier.id}
                          item
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            paddingLeft: "8px",
                          }}
                        >
                          <Typography component="div" sx={{ fontSize: "13px" }}>
                            {qualifier.name}
                          </Typography>
                          <Tooltip title="Delete Qualifier" placement="top">
                            <CloseIcon
                              fontSize="small"
                              className="delete-icon qualifier"
                              onClick={() =>
                                handleDeleteQualifier(qualifier.id)
                              }
                            />
                          </Tooltip>
                        </Grid>
                      );
                    })}
              </Grid>
            </Grid>

            {/* Intents */}
            <Grid
              container
              flexDirection="column"
              sx={{
                border: "1px solid #ced3da",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "8px 16px",
                  marginBottom: "10px",
                  borderBottom: "1px solid #ced3da",
                }}
              >
                Intents:
              </Box>
              <Grid
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{ padding: "8px 16px 12px" }}
              >
                <Grid
                  item
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    paddingBottom: "8px",
                  }}
                >
                  <FormControl className="custom-select" fullWidth>
                    <CuvoInputLabel label="Intent" />
                    <Select
                      fullWidth
                      sx={{
                        height: "38px",
                        borderColor: "#ced3da",
                        borderRadius: "8px",
                      }}
                      name="intent"
                      onChange={handleIntentChange}
                      value={selectedIntent}
                    >
                      {intentList &&
                        intentList
                          .filter(
                            (i: Intent) =>
                              i.id && !formik.values.intents?.includes(i.id)
                          )
                          .map((i) => (
                            <MenuItem key={i.id} value={i.id}>
                              {i.name}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                  <Button
                    disabled={!selectedIntent}
                    sx={{ minHeight: "36px" }}
                    variant="outlined"
                    onClick={handleAddIntent}
                  >
                    Add
                  </Button>
                </Grid>

                {formik.values.intents &&
                  intentList &&
                  intentList
                    .filter(
                      (i: Intent) =>
                        i.id && formik.values.intents?.includes(i.id)
                    )
                    .map((intent) => {
                      return (
                        <Grid
                          key={intent.id}
                          item
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            paddingLeft: "8px",
                          }}
                        >
                          <Typography component="div" sx={{ fontSize: "13px" }}>
                            {intent.name}
                          </Typography>
                          <Tooltip title="Delete Intent" placement="top">
                            <CloseIcon
                              fontSize="small"
                              className="delete-icon intent"
                              onClick={() => handleDeleteIntent(intent.id)}
                            />
                          </Tooltip>
                        </Grid>
                      );
                    })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Box className={isLoading ? "table loader" : "table"}>
        {questionsList.length ? (
          <Box className="products-table table-wrap">
            <Table sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Question test</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Type</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Options</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Qualifiers</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Intents</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {questionsList.map((question) => {
                  return (
                    <TableRow key={question.id}>
                      <TableCell>
                        <Box sx={{ fontWeight: 500, fontSize: "13px" }}>
                          {question.questionText}
                        </Box>
                        {question.helpText ? (
                          <Box
                            sx={{
                              fontSize: "12px",
                              color: "grey",
                              marginTop: "2px",
                            }}
                          >
                            {question.helpText}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell>{question.type}</TableCell>
                      <TableCell>
                        {question.options
                          ? question.options.map((o) => {
                              return (
                                <Box key={o.id} sx={{ padding: "1px 0" }}>
                                  {o.value}
                                </Box>
                              );
                            })
                          : null}
                      </TableCell>
                      <TableCell>
                        {qualifierList.length
                          ? qualifierList
                              .filter(
                                (q: Qualifier) =>
                                  q.id &&
                                  question.qualifiers &&
                                  question.qualifiers.includes(q.id)
                              )
                              .map((q) => {
                                return (
                                  <Box key={q.id} sx={{ padding: "1px 0" }}>
                                    {q.name}
                                  </Box>
                                );
                              })
                          : null}
                      </TableCell>
                      <TableCell>
                        {intentList.length
                          ? intentList
                              .filter(
                                (i: Intent) =>
                                  i.id &&
                                  question.intents &&
                                  question.intents.includes(i.id)
                              )
                              .map((i) => {
                                return (
                                  <Box key={i.id} sx={{ padding: "1px 0" }}>
                                    {i.name}
                                  </Box>
                                );
                              })
                          : null}
                      </TableCell>
                      <TableCell>
                        <List
                          className="table-options"
                          sx={{ display: "flex" }}
                        >
                          <Tooltip title="Edit Question" placement="top">
                            <ListItem
                              onClick={() => handleEditQuestion(question)}
                            >
                              <i className="icon-edit" />
                            </ListItem>
                          </Tooltip>

                          <Tooltip title="Delete Question" placement="top">
                            <ListItem
                              onClick={() => handleDeleteQuestion(question.id)}
                            >
                              <i className="icon-delete" />
                            </ListItem>
                          </Tooltip>
                        </List>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box
            className="products-table table-wrap"
            sx={{
              padding: 2,
              fontSize: "13px",
              backgroundColor: "rgba(0, 0, 0, 0.03)",
            }}
          >
            No questions found!
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default withSnackbar(Questions);
